import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/hooks/use-theme";

export type TurnstileGateHandle = {
  /** Reset widget (call after a submit — tokens are single-use). */
  reset: () => void;
  /** Current token, if the challenge has been solved. */
  getToken: () => string | null;
  /** Ensure a fresh token; resets then resolves when Turnstile re-executes. */
  ensureToken: (timeoutMs?: number) => Promise<string | null>;
};

type Props = {
  onToken?: (token: string | null) => void;
  className?: string;
  action?: string;
};

/**
 * Cloudflare Turnstile widget. Site key is read from
 * `VITE_TURNSTILE_SITE_KEY` — never hardcoded. When missing the
 * widget renders nothing so local dev works without captcha.
 */
export const TurnstileGate = forwardRef<TurnstileGateHandle, Props>(function TurnstileGate(
  { onToken, className, action },
  ref,
) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const instanceRef = useRef<TurnstileInstance | null>(null);
  const pendingRef = useRef<Array<(tok: string | null) => void>>([]);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  const resolvePending = useCallback((tok: string | null) => {
    const q = pendingRef.current;
    pendingRef.current = [];
    q.forEach((fn) => fn(tok));
  }, []);

  useImperativeHandle(ref, () => ({
    reset: () => {
      try { instanceRef.current?.reset(); } catch {}
      setToken(null);
      setFailed(false);
      onToken?.(null);
    },
    getToken: () => token,
    ensureToken: (timeoutMs = 8000) => new Promise<string | null>((resolve) => {
      if (!siteKey) return resolve(null);
      if (token) return resolve(token);
      pendingRef.current.push(resolve);
      try { instanceRef.current?.execute(); } catch {}
      setTimeout(() => {
        const idx = pendingRef.current.indexOf(resolve);
        if (idx >= 0) {
          pendingRef.current.splice(idx, 1);
          resolve(null);
        }
      }, timeoutMs);
    }),
  }), [token, onToken, siteKey]);

  if (!siteKey) return null;

  const lang = (i18n.language || "en").startsWith("ar") ? "ar" : "en";

  return (
    <div className={className}>
      <Turnstile
        ref={instanceRef}
        siteKey={siteKey}
        options={{
          theme: theme === "dark" ? "dark" : "light",
          language: lang,
          size: "flexible",
          action,
        }}
        onSuccess={(tok) => {
          setToken(tok);
          setFailed(false);
          onToken?.(tok);
          resolvePending(tok);
        }}
        onError={() => {
          setFailed(true);
          setToken(null);
          onToken?.(null);
          resolvePending(null);
        }}
        onExpire={() => {
          setToken(null);
          onToken?.(null);
        }}
      />
      {failed ? (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {t("auth.captchaFailed", "Security check failed. Please refresh and try again.")}
        </p>
      ) : null}
    </div>
  );
});

/** Localized message used when a submit is attempted before the challenge resolves. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function captchaRequiredMessage(t: any): string {
  return String(t("auth.captchaRequired", "Please complete the security check to continue."));
}
